import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion, useInView } from "framer-motion";
import type { IconType } from "react-icons";
import {
    FaEnvelope,
    FaFacebook,
    FaGoogle,
    FaPenFancy,
    FaSearch,
    FaShareAlt,
} from "react-icons/fa";
import { useCountUp } from "../hooks/useCountUp";
import {
    solScaleIn,
    solSlideUp,
    solStagger,
    solViewport,
} from "../components/solutions/shared/solutionMotion";
// import { ServicePortfolioGallery } from "../components/solutions/shared/ServicePortfolioGallery";
// import {
//     DIGITAL_MARKETING_PORTFOLIO_SUBTITLE,
//     digitalMarketingPortfolioItems,
// } from "../data/servicePortfolioGalleries";
import "../styles/solution-pages-base.css";
import "../styles/service-portfolio.css";
import "../styles/digital-marketing-solution.css";

const services: { name: string; desc: string; icon: IconType }[] = [
    { name: "SEO", desc: "Rank higher with technical audits, content strategy, and on-page optimization.", icon: FaSearch },
    { name: "Social Media Marketing", desc: "Grow communities and brand presence across key social platforms.", icon: FaShareAlt },
    { name: "Google Ads", desc: "Capture high-intent search traffic with optimized PPC campaigns.", icon: FaGoogle },
    { name: "Meta Ads", desc: "Reach targeted audiences on Facebook and Instagram with creative funnels.", icon: FaFacebook },
    { name: "Email Marketing", desc: "Nurture leads and drive repeat business with automated email flows.", icon: FaEnvelope },
    { name: "Content Marketing", desc: "Build authority with blogs, guides, and conversion-focused content.", icon: FaPenFancy },
];

const funnelSteps = [
    { label: "Awareness", width: 100 },
    { label: "Interest", width: 82 },
    { label: "Leads", width: 64 },
    { label: "Conversion", width: 46 },
    { label: "Retention", width: 32 },
];

export const DigitalMarketingPage = () => {
    const navigate = useNavigate();
    const dashRef = React.useRef<HTMLDivElement>(null);
    const dashInView = useInView(dashRef, solViewport);
    const leads = useCountUp(1240, 2000, dashInView);
    const campaigns = useCountUp(48, 1600, dashInView);
    const roas = useCountUp(420, 2200, dashInView);

    return (
        <div className="sol-page dm-page">
            {/* Section 1 – Marketing Growth Dashboard */}
            <section className="dm-dashboard" ref={dashRef}>
                <div className="sol-container">
                    <motion.div
                        className="dm-dashboard__head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <div className="dm-hero-heading">
                            <h1 className="dm-hero-title">Digital Marketing</h1>
                            <p className="dm-hero-subtitle">
                                Data-driven growth that delivers <span className="dm-accent">ROI</span>
                            </p>
                        </div>
                        <p className="dm-lead">
                            Campaigns, analytics, and creative strategy — unified in one performance dashboard
                            built for measurable business outcomes.
                        </p>
                    </motion.div>

                    <motion.div
                        className="dm-dashboard__panel"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solScaleIn}
                    >
                        <div className="dm-metrics">
                            <div className="dm-metric">
                                <span className="dm-metric__label">Leads Generated</span>
                                <strong>{leads.toLocaleString()}+</strong>
                            </div>
                            <div className="dm-metric">
                                <span className="dm-metric__label">Active Campaigns</span>
                                <strong>{campaigns}</strong>
                            </div>
                            <div className="dm-metric dm-metric--highlight">
                                <span className="dm-metric__label">Avg. ROAS</span>
                                <strong>{roas}%</strong>
                            </div>
                        </div>

                        <div className="dm-charts">
                            {[72, 48, 88, 56, 94, 68].map((h, i) => (
                                <motion.div
                                    key={i}
                                    className="dm-chart-bar"
                                    initial={{ height: 0 }}
                                    whileInView={{ height: `${h}%` }}
                                    viewport={solViewport}
                                    transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                />
                            ))}
                        </div>

                        <div className="dm-chart-legend">
                            <span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span><span>May</span><span>Jun</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Section 2 – Services Grid */}
            <section className="dm-services">
                <div className="sol-container">
                    <motion.div
                        className="dm-services__head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="dm-h2">Marketing Services</h2>
                        <p className="dm-sub">Full-funnel capabilities to attract, convert, and retain customers.</p>
                    </motion.div>

                    <motion.div
                        className="dm-services__grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solStagger}
                    >
                        {services.map((svc, i) => {
                            const Icon = svc.icon;
                            return (
                            <motion.div
                                key={svc.name}
                                className={`dm-service-tile dm-service-tile--${i + 1}`}
                                variants={solScaleIn}
                                custom={i * 0.06}
                            >
                                <span className="dm-service-tile__icon" aria-hidden="true">
                                    <Icon />
                                </span>
                                <h3>{svc.name}</h3>
                                <p>{svc.desc}</p>
                            </motion.div>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* Section 3 – Marketing Funnel */}
            <section className="dm-funnel">
                <div className="sol-container">
                    <motion.div
                        className="dm-funnel__head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="dm-h2">Marketing Funnel</h2>
                        <p className="dm-sub">Every stage optimized for progression and measurable conversion.</p>
                    </motion.div>

                    <div className="dm-funnel__viz">
                        {funnelSteps.map((step, i) => (
                            <motion.div
                                key={step.label}
                                className="dm-funnel__stage"
                                initial={{ opacity: 0, scaleX: 0.3 }}
                                whileInView={{ opacity: 1, scaleX: 1 }}
                                viewport={solViewport}
                                transition={{ delay: i * 0.12, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                                style={{ width: `${step.width}%` }}
                            >
                                <span>{step.label}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Work section — temporarily hidden
            <ServicePortfolioGallery
                items={digitalMarketingPortfolioItems}
                subtitle={DIGITAL_MARKETING_PORTFOLIO_SUBTITLE}
                sectionId="dm-portfolio"
            />
            */}

            <section className="sol-cta">
                <div className="sol-container">
                    <motion.div
                        className="sol-cta__inner"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={solViewport}
                    >
                        <h2 className="sol-cta__title">Ready to accelerate your marketing?</h2>
                        <p className="sol-cta__desc">
                            Let&apos;s audit your channels and build a strategy that drives qualified leads and revenue.
                        </p>
                        <Button className="sol-btn" onClick={() => navigate("/contact")}>
                            Get a Marketing Plan <ArrowRightOutlined />
                        </Button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};
